import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, message } from 'antd';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { CHANGE_PASSWORD_MUTATION } from 'graphql/mutations/auth';
import { removeAuth } from 'utils/auth';

const PASSWORD_MIN_LENGTH = 10;

const validatePasswordComplexity = (password: string): boolean => {
    if (password.length < PASSWORD_MIN_LENGTH) return false;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    return hasUpper && hasLower && hasDigit && hasSpecial;
};

interface ChangePasswordForm {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const ChangePasswordPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [changePassword, { loading }] = useMutation<{ portalChangePassword: boolean }>(CHANGE_PASSWORD_MUTATION);

    const handleSubmit = async (values: ChangePasswordForm) => {
        try {
            await changePassword({
                variables: {
                    oldPassword: values.oldPassword,
                    newPassword: values.newPassword,
                },
            });
            removeAuth();
            message.success(t('changePassword.success'));
            navigate('/login', { replace: true });
        } catch {
            // Apollo errorLink handles error display
        }
    };

    return (
        <div className="flex items-start justify-center pt-12">
            <Card className="w-[460px]">
                <h1 className="text-2xl font-bold">{t('changePassword.title')}</h1>
                <p className="text-gray-400 mt-2 mb-8">{t('changePassword.subtitle')}</p>
                <Form onFinish={handleSubmit} layout="vertical">
                    <Form.Item
                        label={t('changePassword.oldPassword.label')}
                        name="oldPassword"
                        rules={[{ required: true, message: t('changePassword.oldPassword.required') as string }]}
                    >
                        <Input.Password size="large" placeholder={t('changePassword.oldPassword.placeholder') as string} />
                    </Form.Item>
                    <Form.Item
                        label={t('changePassword.newPassword.label')}
                        name="newPassword"
                        rules={[
                            { required: true, message: t('changePassword.newPassword.required') as string },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value) return Promise.resolve();
                                    if (value === getFieldValue('oldPassword')) {
                                        return Promise.reject(new Error(t('changePassword.sameAsOld') as string));
                                    }
                                    if (!validatePasswordComplexity(value)) {
                                        return Promise.reject(new Error(t('changePassword.complexity') as string));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <Input.Password size="large" placeholder={t('changePassword.newPassword.placeholder') as string} />
                    </Form.Item>
                    <Form.Item
                        label={t('changePassword.confirmPassword.label')}
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: t('changePassword.confirmPassword.required') as string },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('changePassword.confirmPassword.mismatch') as string));
                                },
                            }),
                        ]}
                    >
                        <Input.Password size="large" placeholder={t('changePassword.confirmPassword.placeholder') as string} />
                    </Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                        loading={loading}
                        className="rounded-lg h-12 mt-4"
                    >
                        {t('changePassword.submit')}
                    </Button>
                </Form>
            </Card>
        </div>
    );
};

export default ChangePasswordPage;
